/*
 * @Author: AI Assistant
 * @Date: 2024-08-01
 * @Description: 交互模式管理器
 */

import { fabric } from 'fabric';
import { IEditor, IPluginTempl } from 'board-core';
import { BaseInteractionPlugin } from './base/BaseInteractionPlugin';
import { TemporaryLayerManager } from './base/TemporaryLayer';
import { BatchScheduler, BatchProcessor } from '../utils/BatchProcessor';

// 交互模式枚举
export enum InteractionMode {
  /**
   * 指针/选择模式
   */
  POINTER = 'POINTER',
  /**
   * 拖拽模式
   */
  DRAG = 'DRAG',
  /**
   * 缩放模式
   */
  ZOOM = 'ZOOM',
  /**
   * 自由绘制模式
   */
  FREE_DRAWING = 'FREE_DRAWING',
  /**
   * 涂抹模式
   */
  PAINT = 'PAINT',
  /**
   * 矩形框选
   */
  RECT_SELECT = 'RECT_SELECT',
  /**
   * 圆形框选
   */
  CIRCLE_SELECT = 'CIRCLE_SELECT',
  /**
   * 三角形框选
   */
  // TRIANGLE_SELECT = 'TRIANGLE_SELECT',
  /**
   * 套索模式
   */
  LASSO = 'LASSO',
  /**
   * 涂抹橡皮擦
   */
  PAINT_ERASER = 'PAINT_ERASER',
  /**
   * 添加矩形
   */
  RECT_SHAPE = 'RECT_SHAPE',
  /**
   * 添加圆形
   */
  CIRCLE_SHAPE = 'CIRCLE_SHAPE',
  /**
   * 添加三角形
   */
  TRIANGLE_SHAPE = 'TRIANGLE_SHAPE',
  /**
   * 橡皮擦模式
   */
  ERASER = 'ERASER',
  /**
   * 调整大小模式
   */
  RESIZE = 'RESIZE',
  /**
   * 文字插入模式：点击画布空白处创建文本框
   */
  TEXT = 'TEXT',
  /**
   * 线条绘制模式
   */
  LINE = 'LINE',
}

export enum MaskSelectionState {
  SINGLE = 'SINGLE',
  MULTI = 'MULTI',
  SUBTRACT = 'SUBTRACT',
}

declare module 'board-core' {
  interface IEditor {
    // 设置交互模式 可强制设置
    setInteractionMode(mode: InteractionMode, force?: boolean): void;
    // 获取交互模式插件
    getInteractionPlugin?<T extends BaseInteractionPlugin>(mode: InteractionMode): T | undefined;
    getTemporaryLayerManager?(): TemporaryLayerManager | null;
    // 获取当前交互对象ID
    getCurrentInteractionObjectId?(): string | null;
    // 设置当前交互对象ID
    setCurrentInteractionObjectId?(objectId: string | null): void;
    setMaskSelectionState?(state: MaskSelectionState): void;
    getMaskSelectionState?(): MaskSelectionState;
  }
}

export class InteractionModeManager implements IPluginTempl {
  static pluginName = 'InteractionModeManager';
  static events = [];
  static apis = ['setInteractionMode', 'registerPlugin', 'getInteractionPlugin', 'getCurrentInteractionObjectId', 'setCurrentInteractionObjectId', 'setMaskSelectionState', 'getMaskSelectionState'];

  private currentMode: InteractionMode = InteractionMode.POINTER;
  private plugins: Map<InteractionMode, BaseInteractionPlugin> = new Map();
  private currentInteractionObjectId: string | null = null;
  private maskSelectionState: MaskSelectionState = MaskSelectionState.MULTI;
  private objectEventedState = new WeakMap<fabric.Object, boolean>();
  private canvasSkipTargetFindBeforeInteraction = false;
  private isCanvasTargetFindingDisabled = false;

  private _enableSelectBatch: BatchProcessor<fabric.Object>;
  private _disableSelectBatch: BatchProcessor<fabric.Object>;

  constructor(public canvas: fabric.Canvas, public editor: IEditor, scheduler?: BatchScheduler) {
    const createProcessor = scheduler
      ? <U>(opts: ConstructorParameters<typeof BatchProcessor<U>>[0]) => scheduler.createProcessor<U>(opts)
      : <U>(opts: ConstructorParameters<typeof BatchProcessor<U>>[0]) => new BatchProcessor<U>(opts);

    this._enableSelectBatch = createProcessor<fabric.Object>({
      getItems: () => this.canvas.getObjects(),
      processItem: (obj) => {
        obj.evented = this.restoreObjectEvented(obj);
        if (obj.id === 'workspace') {
          obj.selectable = false;
        } else if ((obj as any).lock) {
          obj.selectable = false;
        } else {
          obj.selectable = true;
        }
      },
    });

    this._disableSelectBatch = createProcessor<fabric.Object>({
      getItems: () => this.canvas.getObjects(),
      processItem: (obj) => {
        obj.selectable = false;
        obj.evented = false;
      },
    });

    this.init();
  }

  private init(): void {
    // 将setInteractionMode方法添加到editor
    this.editor.setInteractionMode = this.setInteractionMode.bind(this);
    this.editor.getInteractionPlugin = this.getInteractionPlugin.bind(this);
    this.editor.getCurrentInteractionObjectId = this.getCurrentInteractionObjectId.bind(this);
    this.editor.setCurrentInteractionObjectId = this.setCurrentInteractionObjectId.bind(this);
    this.editor.setMaskSelectionState = this.setMaskSelectionState.bind(this);
    this.editor.getMaskSelectionState = this.getMaskSelectionState.bind(this);
  }

  // 注册交互模式插件
  registerPlugin(mode: InteractionMode, plugin: BaseInteractionPlugin): void {
    this.plugins.set(mode, plugin);
  }

  // 设置交互模式 可强制设置
  setInteractionMode(mode: InteractionMode, force: boolean = false): void {
    if (this.currentMode === mode && !force) return;

    if (this.currentMode === InteractionMode.POINTER) {
      if (mode !== InteractionMode.DRAG) {
        this.canvas.discardActiveObject();
      }
    }

    const currentPlugin = this.plugins.get(this.currentMode);
    if (currentPlugin) {
      currentPlugin.deactivate();
    }

    const maskModes = [InteractionMode.PAINT, InteractionMode.PAINT_ERASER, InteractionMode.RECT_SELECT, InteractionMode.CIRCLE_SELECT, InteractionMode.LASSO];
    const wasMaskMode = maskModes.includes(this.currentMode);
    const isMaskMode = maskModes.includes(mode);

    if (wasMaskMode && !isMaskMode) {
      this.clearAllMasks();
    }

    this.currentMode = mode;

    if (mode === InteractionMode.POINTER) {
      this.restoreCanvasTargetFinding();
      this.canvas.selection = true;
      this.canvas.defaultCursor = 'default';
      this.canvas.hoverCursor = 'move';
      this.canvas.moveCursor = 'move';
      this._disableSelectBatch.cancel();
      this._enableSelectBatch.run();
    } else {
      // NOTE: 先禁用目标并快照 evented，再 activate 插件。
      // 避免插件在 activate 时改写 evented，导致 WeakMap 存到错误的“原始值”。
      this.canvas.selection = false;
      this._enableSelectBatch.cancel();
      this.disableCanvasObjectTargets();
      this._disableSelectBatch.run();

      const newPlugin = this.plugins.get(mode);
      if (newPlugin) {
        newPlugin.activate();
      } else {
        console.warn(`No plugin registered for mode: ${mode}`);
      }
    }

    try {
      this.canvas.requestRenderAll();
    } catch (error) {
      console.warn('Render failed in setInteractionMode:', error);
    }
  }

  // 获取指定交互模式的插件实例
  getInteractionPlugin<T extends BaseInteractionPlugin>(mode: InteractionMode): T | undefined {
    return this.plugins.get(mode) as T | undefined;
  }

  // 获取当前交互模式
  getCurrentMode(): InteractionMode {
    return this.currentMode;
  }

  // 获取当前交互对象ID
  getCurrentInteractionObjectId(): string | null {
    return this.currentInteractionObjectId;
  }

  // 设置当前交互对象ID
  setCurrentInteractionObjectId(objectId: string | null): void {
    this.currentInteractionObjectId = objectId;
  }

  setMaskSelectionState(state: MaskSelectionState): void {
    this.maskSelectionState = state;
  }

  getMaskSelectionState(): MaskSelectionState {
    return this.maskSelectionState;
  }

  private disableCanvasObjectTargets(): void {
    if (!this.isCanvasTargetFindingDisabled) {
      this.canvasSkipTargetFindBeforeInteraction = this.canvas.skipTargetFind;
      this.isCanvasTargetFindingDisabled = true;
    }
    this.canvas.skipTargetFind = true;
    this.canvas.forEachObject((obj) => {
      if (!this.objectEventedState.has(obj)) {
        this.objectEventedState.set(obj, obj.evented ?? true);
      }
      obj.selectable = false;
      obj.evented = false;
    });
  }

  private restoreCanvasTargetFinding(): void {
    if (!this.isCanvasTargetFindingDisabled) return;
    this.canvas.skipTargetFind = this.canvasSkipTargetFindBeforeInteraction;
    this.isCanvasTargetFindingDisabled = false;
  }

  private restoreObjectEvented(obj: fabric.Object): boolean {
    const originalEvented = this.objectEventedState.get(obj);
    this.objectEventedState.delete(obj);

    if (obj.id === 'workspace' || (obj as any).temporaryLayerInfo) return false;
    return originalEvented ?? true;
  }

  // 清除所有掩膜
  private clearAllMasks(): void {
    const maskModes = [InteractionMode.PAINT, InteractionMode.PAINT_ERASER, InteractionMode.RECT_SELECT, InteractionMode.CIRCLE_SELECT, InteractionMode.LASSO];

    // 首先调用各个插件的clearMask方法
    maskModes.forEach(mode => {
      const plugin = this.plugins.get(mode);
      if (plugin && 'clearMask' in plugin && typeof plugin.clearMask === 'function') {
        (plugin as any).clearMask();
      }
    });

    // 额外使用TemporaryLayerManager全局清理所有掩膜相关的临时图层
    // 这确保即使插件的clearMask方法有遗漏，也能彻底清理干净
    const allObjects = this.canvas.getObjects();
    const maskRelatedObjects = allObjects.filter(obj => {
      const tempInfo = (obj as any).temporaryLayerInfo;
      return tempInfo && (
        tempInfo.type === 'rect-preview' ||
        tempInfo.type === 'rect-mask' ||
        tempInfo.type === 'circle-preview' ||
        tempInfo.type === 'circle-mask' ||
        tempInfo.type === 'lasso-preview' ||
        tempInfo.type === 'lasso-mask' ||
        tempInfo.type === 'mask'
      );
    });

    if (maskRelatedObjects.length > 0) {
      this.canvas.remove(...maskRelatedObjects);
      this.canvas.renderAll();
    }
  }

  destroy(): void {
    this.restoreCanvasTargetFinding();

    // 停用所有插件
    for (const plugin of this.plugins.values()) {
      plugin.destroy();
    }

    // 清空插件映射
    this.plugins.clear();
  }
}

export default InteractionModeManager;
